import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LinkedInHeader from '../../assets/linkedin_header.jpg';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });


  return (
    <section ref={ref} className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 dark:text-white mb-4">
            About Me
          </h2>
          <div className="w-20 h-1 bg-primary-600 mx-auto"></div>
        </motion.div>

        {/* Professional Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-16"
        >
          <div className="bg-secondary-50 dark:bg-secondary-900 p-8 rounded-lg shadow-lg dark:shadow-black/20">
            <div className="hidden md:block mb-6 overflow-hidden rounded-lg">
              <img 
                src={LinkedInHeader} 
                alt="LinkedIn Professional Header" 
                className="w-full object-cover h-64 transform hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="text-2xl font-bold text-secondary-800 dark:text-white mb-6">
              Professional Summary
            </h3>
            <p className="text-secondary-600 dark:text-secondary-200 mb-4 leading-relaxed">
              I am a Cybersecurity & Digital Resilience Lead, currently serving as Senior Engineer - Security Operations Center at USIU-Africa, where I established and operationalized the university's SOC from the ground up. I design and build secure, resilient systems across infrastructure, AI, and enterprise environments, turning complex security challenges into practical, scalable solutions.
            </p>
            <p className="text-secondary-600 dark:text-secondary-200 mb-4 leading-relaxed">
              As a builder and enabler of AI-driven security tools, I develop intelligent backend systems that integrate AI models for real-time threat detection, automated response, and data-driven insights. My work spans SOC operations, vulnerability assessment and penetration testing (VAPT), blockchain identity frameworks (SSI/KYC on Cardano), and AI-powered content detection, backed by 3 peer-reviewed publications and certifications including CEH and CHFI.
            </p>
            <p className="text-secondary-600 dark:text-secondary-200 leading-relaxed">
              I am equally committed to growing the next generation of security professionals through mentorship, training, and community. Previously serving as Secretary of Na Malkia, I also drive social and environmental impact initiatives in Nairobi. Building a resilient future requires both technical innovation and human-centered action.
            </p>
          </div>
        </motion.div>

        {/* Skills Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          {/* Technical Skills */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-custom h-full dark:shadow-black/20">
              <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-6">
                Technical Expertise
              </h3>
              <div className="flex flex-wrap gap-2">
                {[
                  'SOC Operations', 'Penetration Testing', 'Vulnerability Assessment',
                  'Digital Forensics', 'DLP Controls', 'Threat Modeling', 'Incident Response',
                  'AI Enablement & Automation', 'Agentic SOC Design',
                  'Python', 'Rust', 'Go', 'Node.js', 'C#', 'PHP (Laravel)', 'Kotlin',
                  'Cardano', 'Smart Contracts', 'SSI / KYC', 'ICP',
                  'SQL', 'PostgreSQL', 'MongoDB', 'Firebase', 'REST APIs', 'React', 'Git'
                ].map((skill, i) => (
                  <span key={i} className="bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-400 px-3 py-1 rounded-full text-sm font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Leadership */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-custom h-full dark:shadow-black/20">
              <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-6">
                Leadership & Community Impact
              </h3>
              <div className="mt-4">
                <h4 className="text-lg font-medium text-secondary-800 dark:text-white mb-3">
                  Professional Leadership:
                </h4>
                <ul className="space-y-2 text-secondary-600 dark:text-secondary-200 mb-6">
                  {[
                    'Senior Engineer - Security Operations Centre (Head of SOC), USIU-Africa',
                    'Technical Services & Client Risk Management',
                    'Cybersecurity Awareness Program Design',
                    'Junior Professional Mentorship'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <h4 className="text-lg font-medium text-secondary-800 dark:text-white mb-3">
                  Na Malkia Initiatives:
                </h4>
                <ul className="space-y-2 text-secondary-600 dark:text-secondary-200">
                  {[
                    'January Food Drive Program',
                    'April Uji Feeding Initiative for Street Families',
                    'Waste Segregation & Environmental Awareness'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Education and Achievements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Education */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-custom h-full dark:shadow-black/20">
              <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-6">
                Education & Certifications
              </h3>
              
              <ul className="space-y-6 text-secondary-600 dark:text-secondary-200">

                <li className="border-l-2 border-primary-500 dark:border-primary-400 pl-4 py-1">
                  <h4 className="text-lg font-medium text-secondary-800 dark:text-white">MSc Information Systems Security</h4>
                  <p className="text-primary-600 dark:text-primary-400">Strathmore University (2022 - 2025)</p>
                </li>

                <li className="border-l-2 border-primary-500 dark:border-primary-400 pl-4 py-1">
                  <h4 className="text-lg font-medium text-secondary-800 dark:text-white">Bachelor of Business Information Technology</h4>
                  <p className="text-primary-600 dark:text-primary-400">Strathmore University (2017 - 2021)</p>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">Second Class Upper</p>
                </li>

                <li className="border-l-2 border-primary-500 dark:border-primary-400 pl-4 py-1">
                  <h4 className="text-lg font-medium text-secondary-800 dark:text-white">Professional Certifications</h4>
                  <ul className="mt-2 space-y-1">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Fundamentals of Risk Management (IRM, 2026)</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Computer Hacking Forensic Investigator (EC Council, 2024)</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Certified Ethical Hacking (EC Council, 2024)</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-primary-500 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Cybersecurity Foundation (Palo Alto, 2022)</span>
                    </li>
                  </ul>
                </li>

              </ul>
            </div>
          </motion.div>

          {/* Achievements */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="bg-white dark:bg-secondary-900 p-6 rounded-lg shadow-custom h-full dark:shadow-black/20">
              <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-6">
                Recent Achievements
              </h3>
              
              <ul className="space-y-4 text-secondary-600 dark:text-secondary-200">
                <li className="flex items-start">
                  <div className="bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 p-2 rounded-full mr-3 flex-shrink-0">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 dark:text-white">Digital Forensics Trainer</h4>
                    <p className="text-sm">IGNITE Accelerator Program (Sep-Oct 2024)</p>
                    <p className="mt-1 text-sm">Led a 7-week program in partnership with ISACA and Scratch and Script, training young professionals in Digital Forensics</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 p-2 rounded-full mr-3 flex-shrink-0">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 dark:text-white">African Emerging Leaders Program</h4>
                    <p className="text-sm">ICANN 80 Conference, Kigali (Jun 2024)</p>
                    <p className="mt-1 text-sm">Participated in the African Emerging Leaders Training Program and ICANN 80 Conference</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 p-2 rounded-full mr-3 flex-shrink-0">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 dark:text-white">Research Publications</h4>
                    <p className="text-sm">2022-2024</p>
                    <p className="mt-1 text-sm">Published 3 peer-reviewed articles on data protection in mobile money, generative AI for harmful content detection, and blockchain for healthcare data interoperability</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 p-2 rounded-full mr-3 flex-shrink-0">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-medium text-secondary-800 dark:text-white">Na Malkia Grant Proposal</h4>
                    <p className="text-sm">2025</p>
                    <p className="mt-1 text-sm">Co-led the design and grant proposal that secured KES 50,000 to scale up Na Malkia's waste segregation and environmental awareness activities</p>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;

